import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';

import { useAppointmentModalActions, useAppointmentModalState } from '../store';
import { TAppointmentDateTimeSlot, TAppointmentModalState } from '../types';

import { defaultAppointmentModalState, extractDateAndTimeSlots } from '../utils';

export function useAppointmentModalStore(
  initialData: TAppointmentModalState,
  dateTimeSlots?: TAppointmentDateTimeSlot[],
  fetchTimeSlotsForProject?: (projectId: number) => Promise<TAppointmentDateTimeSlot[]>,
  locale?: string,
) {
  const {
    setStep,
    setSelectedDate,
    setSelectedTime,
    resetStore,
    updateStore,
    setDateOptions,
    setTimeOptions,
    setSlotsByDate,
    setFirstDate,
    getTimeSlotsForDate,
  } = useAppointmentModalActions();
  const { isOpen, projectId, dateTimeSlot, dateOptions } = useAppointmentModalState();

  // track isOpen change locally
  const localIsOpen = useRef(false);
  const localFirstOpen = useRef<boolean>(false);
  // track projectId change locally
  const localProjectId = useRef<number | undefined>(undefined);
  const [localProjectIdChanged, setLocalProjectIdChanged] = useState(false);

  // assign initialData when open first time
  useEffect(() => {
    resetStore();
    const firstOpen = localIsOpen.current === false && isOpen === true;
    if (firstOpen) {
      updateStore({
        projectId: initialData.projectId,
        appointmentType: initialData.appointmentType || defaultAppointmentModalState.appointmentType,
        duration: initialData.duration || defaultAppointmentModalState.duration,
        notes: initialData.notes || defaultAppointmentModalState.notes,
        name: initialData.name || defaultAppointmentModalState.name,
        email: initialData.email || defaultAppointmentModalState.email,
        phone: initialData.phone || defaultAppointmentModalState.phone,
        dateTimeSlot: initialData.dateTimeSlot,
        selectedDate: initialData.selectedDate,
      });
      localProjectId.current = initialData.projectId; // first projectId assignment
      localIsOpen.current = true; // modal remains in open state
      localFirstOpen.current = true;
      setLocalProjectIdChanged(true); // trigger projectId change
    }
  }, [initialData, isOpen]);
  // track modal close locally
  useEffect(() => {
    if (isOpen === false) {
      localIsOpen.current = false;
      localFirstOpen.current = false;
      setLocalProjectIdChanged(false);
      setDateOptions([]);
      setTimeOptions([]);
      resetStore();
      setStep(1)
    }
  }, [isOpen]);
  // track projectId change locally when modal is open
  useEffect(() => {
    if (projectId !== localProjectId.current && localIsOpen.current) {
      localProjectId.current = projectId;
      localFirstOpen.current = false;
      setLocalProjectIdChanged(true);
      setDateOptions([]);
      setTimeOptions([]);
    }
  }, [projectId]);

  useEffect(() => {
    // allow only when projectId changed and modal is open
    if (!(localProjectIdChanged && localIsOpen.current)) return;

    const initDateTimeSlots = async () => {
      let slots: TAppointmentDateTimeSlot[] = [];
      if (dateTimeSlots) {
        slots = dateTimeSlots;
      } else if (projectId && fetchTimeSlotsForProject) {
        slots = await fetchTimeSlotsForProject(projectId).catch((err) => {
          console.error('Error fetching time slots:', err);
          return [];
        });
      }

      const { dateOptions, slotsByDate } = extractDateAndTimeSlots(slots, locale, dateTimeSlot);
      setDateOptions(dateOptions);
      setSlotsByDate(slotsByDate);

      if (initialData?.selectedDate && localFirstOpen.current) {
        const selectedDateStr = dayjs(initialData.selectedDate).format('YYYY-MM-DD');

        // Find if this date exists in our available dateOptions
        const matchingDateOption = dateOptions.find(
          (option) => dayjs(option.date).format('YYYY-MM-DD') === selectedDateStr,
        );

        if (matchingDateOption) {
          setSelectedDate(matchingDateOption.date);
          const timeOptions = getTimeSlotsForDate(matchingDateOption.date, slotsByDate);
          setTimeOptions(timeOptions);
          if (timeOptions.length > 0) {
            setSelectedTime(timeOptions[0]);
          }
        } else {
          // If selected date is not available, use first available date
          if (dateOptions.length > 0) {
            const firstDate = dateOptions[0].date;
            setSelectedDate(firstDate);

            const timeOptions = getTimeSlotsForDate(firstDate, slotsByDate);
            setTimeOptions(timeOptions);

            if (timeOptions.length > 0) {
              setSelectedTime(timeOptions[0]);
            }
          }
        }
      } else if (projectId === initialData.projectId && initialData?.dateTimeSlot?.start) {
        // initialData project has a dateTimeSlot
        const initialStartDate = new Date(initialData.dateTimeSlot.start);

        // Find if this date exists in our available dateOptions
        const matchingDateOption = dateOptions.find(
          (option) => option.date.toDateString() === initialStartDate.toDateString(),
        );

        if (matchingDateOption) {
          setSelectedDate(matchingDateOption.date);
          // matching time slot or first available
          const timeOptions = getTimeSlotsForDate(matchingDateOption.date, slotsByDate);
          setTimeOptions(timeOptions);
          const matchingTimeSlot = timeOptions.find(
            (slot) => slot.start === initialData.dateTimeSlot?.start,
          );
          if (matchingTimeSlot) {
            setSelectedTime(matchingTimeSlot);
          } else if (timeOptions.length > 0) {
            setSelectedTime(timeOptions[0]);
          }
        } else {
          setFirstDate(dateOptions);
        }
      } else {
        setFirstDate(dateOptions);
        const timeOptions = getTimeSlotsForDate(dateOptions[0].date, slotsByDate);
        setTimeOptions(timeOptions);
      }
    };
    initDateTimeSlots();

    setLocalProjectIdChanged(false);
  }, [
    localProjectIdChanged,
    initialData,
    projectId,
    fetchTimeSlotsForProject,
    dateTimeSlots,
    locale,
    dateOptions,
  ]);
}
